/**
 * FinaliseAndPrintButton
 *
 * Finalises an invoice without emailing it and opens the resulting PDF
 * in a new tab. Rendered alongside "Send Now" in the Send Invoice step.
 */

import { useCallback, useState } from 'react';
import { Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { base64ToBlob, getStoredInvoicePdf, openPdfInNewTab } from '@/services/invoice-pdf-service';
import { cn } from '@/lib/utils';

export interface FinaliseAndPrintButtonProps {
  /** Finalises the invoice (without email) and returns the generated PDF */
  onBeforeSend: (options?: { finaliseWithoutEmail?: boolean }) => Promise<{ success: boolean; pdfBase64?: string; invoiceNumber?: string }>;
  invoiceId?: number | string | null;
  invoiceNumber?: string;
  /** Called once the invoice has been finalised */
  onSent?: () => void;
  /** Disables the button while another action (e.g. a send) is in flight */
  disabled?: boolean;
  /** Reports the finalising state so sibling actions can disable themselves */
  onFinalisingChange?: (isFinalising: boolean) => void;
}

export function FinaliseAndPrintButton({
  onBeforeSend,
  invoiceId,
  invoiceNumber,
  onSent,
  disabled,
  onFinalisingChange,
}: FinaliseAndPrintButtonProps) {
  const [isFinalising, setIsFinalising] = useState(false);

  const handleFinaliseAndPrint = useCallback(async () => {
    setIsFinalising(true);
    onFinalisingChange?.(true);
    try {
      const result = await onBeforeSend({ finaliseWithoutEmail: true });
      if (!result.success) return;

      let pdfBlob: Blob | undefined;
      if (result.pdfBase64) {
        pdfBlob = base64ToBlob(result.pdfBase64);
      } else {
        const pdfKey = invoiceId != null ? String(invoiceId) : invoiceNumber;
        if (pdfKey) {
          pdfBlob = (await getStoredInvoicePdf(pdfKey))?.pdfBlob ?? undefined;
        }
      }

      const effectiveInvoiceNumber = result.invoiceNumber || invoiceNumber || `Invoice-${invoiceId ?? 'invoice'}`;
      if (pdfBlob) {
        openPdfInNewTab(pdfBlob);
        toast.success(`Invoice ${effectiveInvoiceNumber} finalised.`);
      } else {
        toast.error(`Invoice ${effectiveInvoiceNumber} was finalised, but the PDF could not be opened. You can download it from the invoice list.`);
      }
      onSent?.();
    } catch {
      toast.error('Failed to finalise invoice. Please try again.');
    } finally {
      setIsFinalising(false);
      onFinalisingChange?.(false);
    }
  }, [onBeforeSend, invoiceId, invoiceNumber, onSent, onFinalisingChange]);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleFinaliseAndPrint}
      disabled={disabled || isFinalising}
      aria-busy={isFinalising}
      className={cn(isFinalising && 'cursor-wait')}
    >
      {isFinalising ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Printer className="h-4 w-4 mr-2" />
      )}
      {isFinalising ? 'Finalising…' : 'Finalise & Print'}
    </Button>
  );
}
